import { FirebaseServices } from "./config";
import { callFunction } from "./helpers";
import {
  ICreateTagRequest,
  ICreateTagResponse,
  IEditTagRequest,
  IEditTagResponse,
  IDeleteTagRequest,
  IDeleteTagResponse,
  IGetTagsRequest,
  IGetTagsResponse,
  IGetAllTagsRequest,
  IGetAllTagsResponse,
  IAddTagToEntityRequest,
  IAddTagToEntityResponse,
  IRemoveTagFromEntityRequest,
  IRemoveTagFromEntityResponse,
} from "common247";

/**
 * Creates a new tag in the user's tag collection.
 *
 * @param {FirebaseServices} services - Initialized Firebase client services.
 * @param {ICreateTagRequest} params - Payload to create tag.
 * @returns {Promise<ICreateTagResponse>} Created tag.
 */
export async function createTag(
  services: FirebaseServices,
  params: ICreateTagRequest
): Promise<ICreateTagResponse> {
  return callFunction<ICreateTagRequest, ICreateTagResponse>(
    services,
    "createTagFunc",
    params
  );
}

/**
 * Updates an existing tag (e.g., changes its display value).
 * Cascades updated values across all tagged entities.
 *
 * @param {FirebaseServices} services - Initialized Firebase client services.
 * @param {IEditTagRequest} params - Update payload.
 * @returns {Promise<IEditTagResponse>} Updated tag.
 */
export async function editTag(
  services: FirebaseServices,
  params: IEditTagRequest
): Promise<IEditTagResponse> {
  return callFunction<IEditTagRequest, IEditTagResponse>(
    services,
    "editTagFunc",
    params
  );
}

/**
 * Deletes a tag owned by the user.
 *
 * @param {FirebaseServices} services - Initialized Firebase client services.
 * @param {IDeleteTagRequest} params - Tag delete payload.
 * @returns {Promise<IDeleteTagResponse>} Deletion status.
 */
export async function deleteTag(
  services: FirebaseServices,
  params: IDeleteTagRequest
): Promise<IDeleteTagResponse> {
  return callFunction<IDeleteTagRequest, IDeleteTagResponse>(
    services,
    "deleteTagFunc",
    params
  );
}

/**
 * Queries tags accessible to the user (own tags, visible item tags, group tags, or platform-wide).
 *
 * @param {FirebaseServices} services - Initialized Firebase client services.
 * @param {IGetTagsRequest} params - Query filters.
 * @returns {Promise<IGetTagsResponse>} Matching tags list.
 */
export async function getTags(
  services: FirebaseServices,
  params: IGetTagsRequest = {}
): Promise<IGetTagsResponse> {
  return callFunction<IGetTagsRequest, IGetTagsResponse>(
    services,
    "getTagsFunc",
    params
  );
}

/**
 * Queries all tags across the platform for administrative inspection.
 *
 * @param {FirebaseServices} services - Initialized Firebase client services.
 * @param {IGetAllTagsRequest} params - Optional tagType or search filters.
 * @returns {Promise<IGetAllTagsResponse>} List of hydrated TagModel items.
 */
export async function getAllTags(
  services: FirebaseServices,
  params: IGetAllTagsRequest = {}
): Promise<IGetAllTagsResponse> {
  return callFunction<IGetAllTagsRequest, IGetAllTagsResponse>(
    services,
    "getAllTagsFunc",
    params
  );
}

/**
 * Associates a tag with an entity (Memory, Media, Contact, Group).
 * Requires editor or owner permissions on the entity.
 *
 * @param {FirebaseServices} services - Initialized Firebase client services.
 * @param {IAddTagToEntityRequest} params - Target entity and tag association details.
 * @returns {Promise<IAddTagToEntityResponse>}
 */
export async function addTagToEntity(
  services: FirebaseServices,
  params: IAddTagToEntityRequest
): Promise<IAddTagToEntityResponse> {
  return callFunction<
    IAddTagToEntityRequest,
    IAddTagToEntityResponse
  >(services, "addTagToEntityFunc", params);
}

/**
 * Removes a tag from an entity.
 * Only the entity owner or the user who added the tag may remove it.
 *
 * @param {FirebaseServices} services - Initialized Firebase client services.
 * @param {IRemoveTagFromEntityRequest} params - Entity and tag removal details.
 * @returns {Promise<IRemoveTagFromEntityResponse>} Removal status.
 */
export async function removeTagFromEntity(
  services: FirebaseServices,
  params: IRemoveTagFromEntityRequest
): Promise<IRemoveTagFromEntityResponse> {
  return callFunction<IRemoveTagFromEntityRequest, IRemoveTagFromEntityResponse>(
    services,
    "removeTagFromEntityFunc",
    params
  );
}

